import { useAnalytics } from "@codat/analytics";
import {
  Button,
  Modal,
  ModalBody,
  Typography,
  useToast,
} from "@codat/orchard-ui";
import { CodatUser, useDeleteUserMutation } from "api/users";
import { AxiosPromise } from "axios";
import React from "react";

import styles from "./DeleteUserModal.module.css";

interface DeleteUserModalProps {
  open: boolean;
  user: CodatUser;
  onCancel: () => void;
  onSuccess: () => void;
}

type DeleteUserModalContentProps = DeleteUserModalProps & {
  isDeleting: boolean;
  deleteUserApi: () => AxiosPromise<CodatUser>;
};

export const DeleteUserModalContent: React.FC<DeleteUserModalContentProps> = ({
  open,
  user,
  onCancel,
  onSuccess,
  isDeleting,
  deleteUserApi,
}) => {
  const { addToast } = useToast();
  const { sendEvent } = useAnalytics();

  const deleteUser = () => {
    sendEvent("Delete Button - Click");
    deleteUserApi()
      .then(() => {
        addToast({
          message: `Deleted user ${user.name}`,
          variant: "success",
        });
        onSuccess();
      })
      .catch((error) => {
        addToast({
          message: `Unable to delete user: ${error}`,
          variant: "error",
        });
      });
  };

  return (
    <Modal
      onClose={() => {
        onCancel();
        sendEvent("Close");
      }}
      isPinned
    >
      {open && user && (
        <ModalBody
          size="medium"
          title="Confirm delete"
          className={styles.card}
          data-testid="delete-user-modal"
          onClose={() => {
            onCancel();
            sendEvent("Close Button - Click");
          }}
        >
          <>
            <Typography
              data-testid="delete-user-modal-body"
              variant="body_md"
              as="h2"
            >
              Are you sure you want to delete {user.name}?
            </Typography>
            <div className={styles.footerContainer}>
              <Button
                data-testid="cancel-button"
                className={styles.cancelButton}
                label="Cancel"
                onClick={() => {
                  onCancel();
                  sendEvent("Cancel Button - Click");
                }}
              />
              <Button
                data-testid="confirm-button"
                className={styles.submitButton}
                label="Delete"
                onClick={deleteUser}
                isLoading={isDeleting}
              />
            </div>
          </>
        </ModalBody>
      )}
    </Modal>
  );
};

const DeleteUserModal: React.FC<DeleteUserModalProps> = (props) => {
  const [{ loading }, deleteUserApi] = useDeleteUserMutation({
    userId: props.user.id,
  });

  return (
    <DeleteUserModalContent
      isDeleting={loading}
      deleteUserApi={deleteUserApi}
      {...props}
    />
  );
};

export default DeleteUserModal;
